/**
 * Formats an IANA time zone (e.g. "Asia/Dubai") as a GMT offset label like
 * "GMT+04:00". Returns '' for an empty/invalid zone instead of throwing.
 *
 * Reads the zone's wall-clock time at the current instant, reinterprets it as
 * UTC, and diffs against the real instant — correct for every zone (including
 * half-hour/quarter-hour zones and the date-line edges) and DST-aware, since
 * the offset is taken from "now".
 */
export const getUTCOffset = (timeZoneValue: string): string => {
   if (!timeZoneValue) return '';
   const date = new Date();
   // Intl throws RangeError on an unknown/invalid zone (e.g. a stale value
   // persisted on a profile), so guard it to honor the documented '' contract.
   let parts: Intl.DateTimeFormatPart[];
   try {
      const formatter = new Intl.DateTimeFormat('en-US', {
         timeZone: timeZoneValue,
         hour12: false,
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
         hour: '2-digit',
         minute: '2-digit',
      });
      parts = formatter.formatToParts(date);
   } catch {
      return '';
   }
   const get = (type: string) =>
      parts.find((part) => part.type === type)?.value;

   const year = get('year');
   const month = get('month');
   const day = get('day');
   const hourPart = get('hour');
   const minutePart = get('minute');
   if (!year || !month || !day || !hourPart || !minutePart) return '';

   // Reinterpret the zone's wall-clock time as if it were UTC, then diff
   // against the real instant. This yields the true offset for every zone
   // (including half-hour zones and the date-line edges) without losing the
   // calendar day the way an hour-only subtraction would. `% 24` guards the
   // "24" some engines emit for midnight under hour12: false.
   const wallClockAsUTC = Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hourPart, 10) % 24,
      parseInt(minutePart, 10)
   );
   // wallClockAsUTC drops sub-minute precision while date.getTime() keeps it,
   // so the raw quotient lands in (R-1, R] for the true offset R. ceil (not
   // round) recovers R exactly for every sign; round under-reports by a
   // minute once the live clock passes :30s (e.g. Dubai flashing GMT+03:59).
   const offsetMinutesTotal =
      Math.ceil((wallClockAsUTC - date.getTime()) / 60000);

   const sign = offsetMinutesTotal >= 0 ? '+' : '-';
   const absOffset = Math.abs(offsetMinutesTotal);
   const offsetHours = Math.floor(absOffset / 60);
   const offsetMinutes = absOffset % 60;

   return `GMT${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
};
